import React, { useState, useEffect } from 'react';
import { 
  ChevronRight,
  X,
  Gift,
} from 'lucide-react';
import { Button } from '../../../Components/UI/button';
import { Badge } from '../../../Components/UI/badge';
import useIsMobile from '../../../Hooks/useIsMobile';
import { RiDiscountPercentFill } from 'react-icons/ri';
import { getCoupons } from '../../../Utils/Utilities';
import CountdownTimer from '../../../Components/Common/CountDownTimer';
import { getTableDateOrWeekday } from '../../../Utils/DateTime';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogClose } from '../../../Components/UI/dialog';
import { ArrowLeft } from 'lucide-react';

const AvailableCoupons = ({ 
  editionID = 1, 
  billingPeriod = 'yearly', 
  changeType = 'SUB',
  onCouponSelect = () => {},
  onClose = () => {},
  open = false  // Add open prop to control from parent
}) => {
  const [showTimer, setShowTimer] = useState(true);
  const isMobile = useIsMobile();

  // Get coupons from utilities
  const staticCoupons = getCoupons(editionID, billingPeriod, changeType);

  const isCouponExpired = (expiryDate) => {
    if (!expiryDate) return false;
    return new Date() > expiryDate;
  };

  const handleClose = () => {
    onClose();
  };

  const handleCouponSelect = (coupon) => {
    // Store in localStorage with correct property name
    localStorage.setItem('AppliedCoupon', JSON.stringify(coupon));
    onCouponSelect(coupon);
    handleClose();
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleClose()} mobilePopup={isMobile}>
      <DialogContent className="overflow-y-auto !bg-white !h-auto !space-y-0 !gap-0">
        <DialogHeader className="!bg-sahi-white !text-sahi-dark-blue !border-0">
          <DialogTitle className="flex items-center gap-2 !text-sahi-dark-blue">
            Available Coupons
          </DialogTitle>

            <DialogClose
              className="h-9 w-9 p-0 text-white hover:bg-white/20 flex items-center justify-center"
              tabIndex={-1}
              onClick={handleClose}
            >
              <X className="h-5 w-5" />
              <span className="sr-only">Close</span>
            </DialogClose>
        </DialogHeader>

        {/* Content Area */}
        <div className="px-4 pb-4 bg-white">
          {staticCoupons.length > 0 ? (
            <div className="space-y-3 sm:space-y-4">
              {staticCoupons.map((coupon) => (
                <div
                  key={coupon.id}
                  className="p-2 sm:p-3 border border-gray-300 rounded-lg bg-bg-grey hover:border-gray-400 transition-all cursor-pointer"
                  onClick={() => handleCouponSelect(coupon)}
                >
                  <div className="flex items-center justify-between gap-2 sm:gap-3">
                    <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
                      <RiDiscountPercentFill 
                        className="text-sahi-green text-3xl sm:text-4xl flex-shrink-0 mt-0.5" 
                        style={{filter: 'drop-shadow(0 0 2px rgba(0,0,0,0.1))'}} 
                      />
                      <div className="flex-1 min-w-0">
                        {/* Desktop: Badge on same line as coupon code */}
                        <div className="hidden sm:flex items-center justify-between gap-2 flex-wrap">
                          <p className="font-semibold text-sm sm:text-base text-gray-900 truncate">
                            {coupon.couponCode}
                          </p>
                          {coupon.couponCode === 'FLASHOFFER' ? (
                            <div className="flex flex-col items-end gap-1">
                              <CountdownTimer 
                                expiryDate={new Date(Date.now() + 24 * 60 * 60 * 1000)}
                                size="sm"
                                showFlash={true}
                                onExpire={() => setShowTimer(false)}
                                text="Offer ends soon!"
                              />
                            </div>
                          ) : coupon.showTimer && coupon.validTillDT && !isCouponExpired(coupon.validTillDT) && showTimer ? (
                            <CountdownTimer 
                              expiryDate={coupon?.id == 6 ? coupon.validTillDT : new Date(Date.now() + 24 * 60 * 60 * 1000)} 
                              size="sm"
                              showFlash={true}
                              text={coupon?.id == 6 ? "" : "Limited Time Offer!"}
                              onExpire={() => setShowTimer(false)}
                            />
                          ) : coupon.validTillDT && (
                            <div className="group relative">
                              <Badge className="bg-red-100 border border-sahi-red text-sahi-red cursor-pointer">
                                Validity: {getTableDateOrWeekday(coupon.validTillDT)}
                              </Badge>
                              <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-sahi-primary-color text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                                Coupon valid till: {getTableDateOrWeekday(coupon.validTillDT)}
                              </div>
                            </div>
                          )}
                        </div>
                        <p className="hidden sm:block text-xs sm:text-sm text-gray-600 line-clamp-2 mt-1">
                          {coupon.description}
                        </p>
                        
                        {/* Mobile: Badge below coupon code */}
                        <div className="sm:hidden flex flex-col gap-1">
                          <p className="font-semibold text-sm text-gray-900">{coupon.couponCode}</p>
                          <p className="text-xs sm:text-sm text-gray-600 line-clamp-2 mt-1">
                            {coupon.description}
                          </p>
                          {coupon.couponCode === 'FLASHOFFER' ? (
                            <div className="flex flex-col items-center gap-1">
                              <CountdownTimer 
                                expiryDate={new Date(Date.now() + 24 * 60 * 60 * 1000)}
                                size="sm"
                                showFlash={true}
                                onExpire={() => setShowTimer(false)}
                                text="Offer ends soon!"
                              />
                            </div>
                          ) : coupon.showTimer && coupon.validTillDT && !isCouponExpired(coupon.validTillDT) && showTimer ? (
                            <CountdownTimer 
                              expiryDate={coupon?.id == 6 ? coupon.validTillDT : new Date(Date.now() + 24 * 60 * 60 * 1000)} 
                              size="sm"
                              showFlash={true}
                              text={coupon?.id == 6 ? "" : "Limited Time Offer!"}
                              onExpire={() => setShowTimer(false)}
                            />
                          ) : coupon.validTillDT && (
                            <Badge className="bg-red-100 border border-sahi-red text-sahi-red cursor-pointer w-fit text-xs">
                              Validity: {getTableDateOrWeekday(coupon.validTillDT)}
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                    <Button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCouponSelect(coupon);
                      }}
                      className="px-3 sm:px-4 py-2 text-xs sm:text-sm font-semibold rounded-md whitespace-nowrap transition-all duration-200 flex-shrink-0 !h-8 bg-sahi-green text-white shadow-md hover:shadow-lg"
                    >
                      Apply
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <p className="text-gray-500 text-sm sm:text-base">
                No coupons available for this selection.
              </p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AvailableCoupons;